// Fixed version with proper initialization and error handling

import Vue from "vue";
import axios from "axios";
import each from "lodash/each";
import filter from "lodash/filter";
import unionBy from "lodash/unionBy";

import {
  Plugins,
  FilesystemDirectory,
  FilesystemEncoding,
} from "@capacitor/core";

const { Storage, Filesystem } = Plugins;

const rootUrl = "https://www.disasterplanning.com";

function readStampFromUrl(url) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      resolve(e.target.result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(url);
  });
}

export default {
  state: {
    stamps: [],
    stamps_ready: false,
    stamp_files: {},
    cached_stamps: [],
    all_cached_stamps: [],
    loaded_stamps: {},
    loading_stamps: false,
    initialization_complete: false, // NEW: Track if initial load is done
  },

  mutations: {
    setStamps(state, data) {
      Vue.set(state, "stamps", data);
      try {
        Storage.set({
          key: "stamps_list",
          value: JSON.stringify(state.stamps),
        });
      } catch (e) {
        console.error("Error setting stamps in storage:", e);
      }
    },

    addToCache(state, { id, media }) {
      Vue.set(state.loaded_stamps, id, media);
    },

    addToStampCache(state, stamp) {
      let current_cache = unionBy(state.all_cached_stamps, [stamp], "id");
      Vue.set(state, "all_cached_stamps", current_cache);
      try {
        Storage.set({
          key: "all_cached_stamps",
          value: JSON.stringify(state.all_cached_stamps),
        });
      } catch (e) {
        console.error("Error adding to stamp cache:", e);
      }
    },

    setStampCache(state, data) {
      Vue.set(state, "loaded_stamps", data);
    },

    setAllStampCache(state, data) {
      Vue.set(state, "all_cached_stamps", data);
      try {
        Storage.set({
          key: "all_cached_stamps",
          value: JSON.stringify(state.all_cached_stamps),
        });
      } catch (e) {
        console.error("Error setting all stamp cache:", e);
      }
    },

    setStampsLoading(state, data) {
      Vue.set(state, "loading_stamps", data);
    },

    setStampsReady(state, data) {
      Vue.set(state, "stamps_ready", data);
    },

    setInitializationComplete(state, data) {
      Vue.set(state, "initialization_complete", data);
    },

    updateStamp(state, data) {
      let current_cache = unionBy([data], state.stamps, "id");
      Vue.set(state, "stamps", current_cache);
      try {
        Storage.set({
          key: "stamps_list",
          value: JSON.stringify(state.stamps),
        });
      } catch (e) {
        console.error("Error updating stamp:", e);
      }
    },

    updateStampCache(state, data) {
      let current_cache = unionBy([data], state.all_cached_stamps, "id");
      Vue.set(state, "all_cached_stamps", current_cache);
      try {
        Storage.set({
          key: "all_cached_stamps",
          value: JSON.stringify(state.all_cached_stamps),
        });
      } catch (e) {
        console.error("Error updating stamp cache:", e);
      }
    },
  },

  actions: {
    // NEW: Initialize stamps on app load
    async initializeStamps({ commit, dispatch, state }) {
      if (state.initialization_complete) {
        return; // Already initialized
      }

      try {
        // Load cached stamps from storage first
        const cachedStampsResponse = await Storage.get({
          key: "all_cached_stamps",
        });
        if (cachedStampsResponse.value) {
          const cachedStamps = JSON.parse(cachedStampsResponse.value);
          if (cachedStamps && Array.isArray(cachedStamps)) {
            commit("setAllStampCache", cachedStamps);
          }
        }

        // Load stamps list from storage
        const stampsListResponse = await Storage.get({ key: "stamps_list" });
        if (stampsListResponse.value) {
          const stampsList = JSON.parse(stampsListResponse.value);
          if (stampsList && Array.isArray(stampsList)) {
            commit("setStamps", stampsList);
          }
        }

        commit("setInitializationComplete", true);
      } catch (e) {
        console.error("Error initializing stamps:", e);
        // Set empty arrays to prevent undefined errors
        commit("setAllStampCache", []);
        commit("setStamps", []);
        commit("setInitializationComplete", true);
      }
    },

    // Get
    async getStampsList({ commit, dispatch, state }) {
      // Ensure initialization is complete
      if (!state.initialization_complete) {
        await dispatch("initializeStamps");
      }

      try {
        const response = await Storage.get({ key: "stamps_list" });
        if (response.value) {
          const list = JSON.parse(response.value);
          if (list && Array.isArray(list) && list.length) {
            commit("setStamps", list);
          }
        }
      } catch (e) {
        console.error("Error getting stamps list:", e);
      }

      dispatch("fetchStamps");
    },

    // Fetch
    async fetchStamps({ commit, rootState }) {
      try {
        const headers = { Authorization: rootState.authorization };
        const response = await axios.get(
          `${rootUrl}/account/annotations/stamps/2/`,
          { headers: headers },
        );

        if (response.status === 200) {
          const new_stamps = [];
          each(response.data, (stamp) => {
            new_stamps.push({
              id: stamp.id,
              label: stamp.label,
              media: stamp.stamp_url,
              filename: stamp.media,
              dirty: true,
              stamp_url: stamp.stamp_url,
            });
          });
          commit("setStamps", new_stamps);
        }

        if (response.status !== 200) {
          console.error("Could not fetch stamps from the API");
          console.error(response);
        }
      } catch (e) {
        console.error("Error fetching stamps:", e);
      }
    },

    async reloadStampCache({ commit, state, dispatch }) {
      // Ensure we have an array to work with
      if (!state.initialization_complete) {
        await dispatch("initializeStamps");
      }

      let new_cache = [];
      try {
        const response = await Storage.get({ key: "all_cached_stamps" });
        if (response.value) {
          new_cache = JSON.parse(response.value);
          if (!Array.isArray(new_cache)) {
            new_cache = [];
          }
          commit("setAllStampCache", new_cache);
        }
      } catch (e) {
        console.error("Error reloading stamp cache:", e);
        new_cache = [];
        commit("setAllStampCache", new_cache);
      }
      return new_cache;
    },

    // Fetch All - FIXED VERSION
    async fetchAllStamps({ commit, dispatch, rootState, state }) {
      // Ensure initialization is complete
      if (!state.initialization_complete) {
        console.warn(
          "initializeStamps",
          "Initializing stamps while fetching all stamps",
        );
        await dispatch("initializeStamps");
      }

      let new_cache = await dispatch("reloadStampCache");
      if (!new_cache || !Array.isArray(new_cache)) {
        new_cache = [];
      }

      try {
        const headers = { Authorization: rootState.authorization };
        const response = await axios.get(
          `${rootUrl}/account/annotations/stamps/`,
          { headers: headers },
        );

        if (response.status === 200 && Array.isArray(response.data)) {
          let needs_fetch = false;

          const syncStartTime = new Date().toISOString();
          console.log("🔄 Starting sync...", syncStartTime);

          // FIXED: Direct iteration instead of using index
          for (const stamp of response.data) {
            // Validate stamp object
            if (!stamp || !stamp.id) {
              console.warn("Invalid stamp object:", stamp);
              continue;
            }

            const find_stamp = (element) => element && element.id === stamp.id;
            const stamp_name = `stamp-${stamp.id}.png`;
            let cacheIndex = new_cache.findIndex(find_stamp);

            try {
              await Filesystem.stat({
                path: stamp_name,
                directory: FilesystemDirectory.Cache,
              });
            } catch (e) {
              console.info(`Missing ${stamp_name}`);
              cacheIndex = -1;
            } 
            if (cacheIndex < 0) {
              needs_fetch = true;
              await commit("addToStampCache", {
                id: stamp.id,
                label: stamp.label,
                media: stamp.stamp_url,
                filename: stamp.media,
                dirty: true,
                stamp_url: stamp.stamp_url,
              });
            }
          }

          if (needs_fetch) {
            await dispatch("fetchStampFiles", commit);
          
          }

          console.log("✅ Background sync complete", new Date().toISOString());
        }

        if (response.status !== 200) {
          console.error("Could not fetch stamps from the API");
          console.error(response);
        }
      } catch (e) {
        console.error("Error in fetchAllStamps:", e);
      }

      return new_cache;
    },

    // Clear
    clearStamps({ commit }) {
      commit("setStamps", []);
      commit("setStampCache", {});
      commit("setAllStampCache", []);
      commit("setStampsLoading", false);
      commit("setStampsReady", false);
      commit("setInitializationComplete", false);
    },

    async fetchStampFiles({ state, dispatch, commit }) {
      let all_stamps_ready = true;

      console.info("Fetching stamps...");

      // Ensure we have an array
      if (!Array.isArray(state.all_cached_stamps)) {
        console.warn("all_cached_stamps is not an array");
        Vue.set(state, "stamps_ready", false);
        return false;
      }

      for (const stamp of state.all_cached_stamps) {
        // Validate stamp object
        if (!stamp || !stamp.id) {
          console.warn("Invalid stamp in cache:", stamp);
          continue;
        }

        const stamp_name = `stamp-${stamp.id}.png`;
        try {
          await Filesystem.stat({
            path: stamp_name,
            directory: FilesystemDirectory.Cache,
          });
          if (!stamp.media || !stamp.media.startsWith("data")) {
            await dispatch("fetchStampFile", stamp);
            all_stamps_ready = false;
          }
        } catch (e) {
          all_stamps_ready = false;
          await dispatch("fetchStampFile", stamp);
        }
      }


      Vue.set(state, "stamps_ready", all_stamps_ready);

      return all_stamps_ready;
    },

    async fetchStampFile({ dispatch }, stamp) {
      // Validate stamp object
      if (!stamp || !stamp.id) {
        console.error("Invalid stamp object passed to fetchStampFile:", stamp);
        return false;
      }

      let data;
      const stamp_name = `stamp-${stamp.id}.png`;

      try {
        await Filesystem.stat({
          path: stamp_name,
          directory: FilesystemDirectory.Cache,
        });
        data = await dispatch("loadStampFromStorage", stamp);
      } catch (e) {
        try {
          const response = await fetch(stamp.stamp_url);
          console.info(`${stamp_name} was not in storage, downloading...`);

          if (response.ok) {
            const blob_data = await response.blob();
            stamp.data = blob_data;
            await dispatch("saveStampToStorage", stamp);
            data = await dispatch("loadStampFromStorage", stamp);
          } else {
            console.error(`Could not download ${stamp.stamp_url}...`);
            return false;
          }
        } catch (fetchError) {
          console.error(`Error fetching stamp file ${stamp_name}:`, fetchError);
          return false;
        }
      }

      return data;
    },

    async loadAssetsForAnnotations(
      { commit, getters, dispatch, state },
      annotations,
    ) {
      // Ensure initialization is complete
      if (!state.initialization_complete) {
        await dispatch("initializeStamps");
      }

      console.log("🎨 Loading stamps for annotations...");

      // Validate input
      if (!annotations || !Array.isArray(annotations)) {
        console.warn("Invalid annotations array");
        return [];
      }

      let stamp_promises = [];
      let seen_ids = [];

      const stampCache = getters.getStampCache;
      if (!Array.isArray(stampCache)) {
        console.warn("Stamp cache is not ready");
        return [];
      }

      // First pass: collect all unique stamp IDs
      for (const page of annotations) {
        if (!Array.isArray(page)) continue;

        for (const layer of page) {
          if (!layer || layer.element !== "stamp" || !layer.stampId) continue;

          if (!seen_ids.includes(layer.stampId)) {
            const stamp = stampCache.find(
              (element) => element && element.id === layer.stampId,
            );
            if (stamp) {
              seen_ids.push(stamp.id);
              // Create promise for parallel loading
              stamp_promises.push(
                dispatch("loadStampFromStorage", stamp)
                  .then((data) => {
                    const stampCopy = Object.assign({}, stamp);
                    stampCopy.media = data;
                    console.log(`✅ Loaded stamp ${stamp.id}`);
                    return stampCopy;
                  })
                  .catch((err) => {
                    console.error(`❌ Failed to load stamp ${stamp.id}:`, err);
                    return null;
                  }),
              );
            }
          }
        }
      }

      // Load all stamps in parallel
      console.log(`⏳ Loading ${stamp_promises.length} stamps in parallel...`);
      const stamp_array = await Promise.all(stamp_promises);
      console.log(`✅ All stamps loaded`);

      // Filter out any failed stamps (null values)
      return stamp_array.filter((stamp) => stamp !== null);
    },

    async loadStampFromStorage({ commit, dispatch }, stamp) {
      // Validate stamp object
      if (!stamp || !stamp.id) {
        console.error(
          "Invalid stamp object passed to loadStampFromStorage:",
          stamp,
        );
        return false;
      }

      const stamp_name = `stamp-${stamp.id}.png`;

      try {
        let contents = await Filesystem.readFile({
          path: stamp_name,
          directory: FilesystemDirectory.Cache,
          encoding: FilesystemEncoding.UTF8,
        });

        stamp.cache_name = stamp_name;
        stamp.dirty = false;

        commit("updateStamp", stamp);
        commit("updateStampCache", stamp);

        return contents.data;
      } catch (e) {
        console.info(`${stamp_name} is not in storage. Downloading...`);
        await dispatch("fetchStampFile", stamp);

        // Try reading again after download
        try {
          let contents = await Filesystem.readFile({
            path: stamp_name,
            directory: FilesystemDirectory.Cache,
            encoding: FilesystemEncoding.UTF8,
          });
          return contents.data;
        } catch (retryError) {
          console.error(`Failed to load stamp after download:`, retryError);
          return false;
        }
      }
    },

    async saveStampToStorage({ commit }, stamp) {
      // Validate stamp object
      if (!stamp || !stamp.id || !stamp.data) {
        console.error(
          "Invalid stamp object passed to saveStampToStorage:",
          stamp,
        );
        return false;
      }

      const stamp_name = `stamp-${stamp.id}.png`;

      try {
        const data = await readStampFromUrl(stamp.data);

        await Filesystem.writeFile({
          path: stamp_name,
          data: data,
          directory: FilesystemDirectory.Cache,
          encoding: FilesystemEncoding.UTF8,
        });

        stamp.cache_name = stamp_name;
        stamp.dirty = false;
        await commit("updateStamp", stamp);
        await commit("updateStampCache", stamp);

        return stamp_name;
      } catch (e) {
        console.error(`Error saving stamp ${stamp_name}:`, e);
        return false;
      }
    },
  },

  getters: {
    getStamps: (state) => {
      return Array.isArray(state.stamps) ? state.stamps : [];
    },

    getStampInCache: (state) => (id) => {
      if (!Array.isArray(state.all_cached_stamps)) return null;
      return state.all_cached_stamps.find((stamp) => {
        return stamp && stamp.id === id;
      });
    },

    getStampCache: (state) => {
      return Array.isArray(state.all_cached_stamps)
        ? state.all_cached_stamps
        : [];
    },

    getStampsReady: (state) => {
      return state.stamps_ready;
    },

    getStampsLoading: (state) => {
      return state.loading_stamps;
    },

    getAllStampsLoaded: (state) => {
      return state.stamps_ready;
    },

    getInitializationComplete: (state) => {
      return state.initialization_complete;
    },

    getStampCacheStatus: (state) => {
      if (!Array.isArray(state.all_cached_stamps)) return [];
      return filter(state.all_cached_stamps, (stamp) => {
        return stamp && !stamp.stamp_name;
      });
    },

    getStampsCached: (state) => {
      if (!Array.isArray(state.all_cached_stamps)) return [];
      return filter(state.all_cached_stamps, (stamp) => {
        return stamp && !!stamp.stamp_name;
      });
    },
  },
};
